import React, { useEffect, useMemo, useState } from "react";
import { Alert, Spinner } from "react-bootstrap";
import { FiCheckCircle, FiClipboard, FiRefreshCw, FiUsers } from "react-icons/fi";
import { getApiUrl } from "../../utils/apiUrl";
import { getCsrfToken } from "../../utils/getCsrfToken";
import "./WhatsappPage.css";

function WhatsappGroupUpdater() {
  const [groups, setGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [inputText, setInputText] = useState("");
  const [status, setStatus] = useState(null);
  const [error, setError] = useState("");
  const [loadingGroups, setLoadingGroups] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetch(getApiUrl("api/whatsapp/groups/"), {
      credentials: "include",
    })
      .then(async (res) => {
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || "No se pudieron cargar los grupos.");
        return data;
      })
      .then(setGroups)
      .catch((fetchError) => setError(fetchError.message))
      .finally(() => setLoadingGroups(false));
  }, []);

  const detectedNumbers = useMemo(() => {
    const matches = inputText.match(/(?:\+?56[\s().-]*)?9(?:[\s().-]*\d){8}/g) || [];
    return [...new Set(matches.map((value) => {
      let digits = value.replace(/\D/g, "");
      if (digits.startsWith("56")) digits = digits.slice(2);
      return digits;
    }).filter((digits) => digits.length === 9))];
  }, [inputText]);

  const handleSubmit = async () => {
    if (!selectedGroup || detectedNumbers.length === 0 || saving) return;
    setSaving(true);
    setError("");
    setStatus(null);
    try {
      const csrfToken = await getCsrfToken();
      const response = await fetch(getApiUrl("api/whatsapp/import/"), {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRFToken": csrfToken,
        },
        credentials: "include",
        body: JSON.stringify({
          group_id: selectedGroup,
          celulares: detectedNumbers,
        }),
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error || "No se pudo actualizar el grupo.");
      setStatus(result);
      setInputText("");
    } catch (submitError) {
      setError(submitError.message);
    } finally {
      setSaving(false);
    }
  };


  return (
    <main className="capsule whatsapp-tool">
      <header className="whatsapp-tool-header">
        <span className="whatsapp-tool-icon"><FiUsers /></span>
        <div>
          <span className="whatsapp-tool-eyebrow">Herramienta de apoyo</span>
          <h2>Actualizar grupo de WhatsApp</h2>
          <p>Pega la lista copiada desde WhatsApp para reemplazar las integrantes del grupo.</p>
        </div>
      </header>

      <label htmlFor="whatsapp-group">Grupo</label>
      <select
        id="whatsapp-group"
        className="form-select"
        onChange={(e) => setSelectedGroup(e.target.value)}
        defaultValue=""
        disabled={loadingGroups || saving}
      >
        <option value="" disabled>{loadingGroups ? "Cargando grupos..." : "Selecciona un grupo"}</option>
        {groups.map((g) => (
          <option key={g.id} value={g.id}>{g.nombre}</option>
        ))}
      </select>

      <div className="whatsapp-tool-label-row">
        <label htmlFor="whatsapp-members">Integrantes copiadas desde WhatsApp</label>
        <span>{detectedNumbers.length} teléfonos detectados</span>
      </div>
      <textarea
        id="whatsapp-members"
        className="form-control"
        rows="9"
        placeholder="Pega aquí la lista o el contenido copiado desde WhatsApp..."
        value={inputText}
        onChange={(e) => setInputText(e.target.value)}
        disabled={saving}
      />

      <p className="whatsapp-tool-note">
        <FiRefreshCw /> Esta acción reemplaza la lista actual. Reconoce Personas y Profesionales por su celular; las Personas archivadas que coincidan volverán a quedar activas.
      </p>

      <button
        type="button"
        className="whatsapp-tool-submit"
        onClick={handleSubmit}
        disabled={!selectedGroup || detectedNumbers.length === 0 || saving}
      >
        {saving ? <><Spinner animation="border" size="sm" /> Actualizando...</> : <><FiClipboard /> Actualizar grupo</>}
      </button>

      {error && <Alert variant="danger" className="mb-0">{error}</Alert>}
      {status && <section className="whatsapp-tool-result" aria-live="polite">
        <div className="whatsapp-tool-result-title"><FiCheckCircle /> <strong>{status.group} actualizado</strong></div>
        <div className="whatsapp-tool-metrics">
          <span><strong>{status.total}</strong> detectados</span>
          <span><strong>{status.results?.maestras?.length || 0}</strong> Maestras</span>
          <span><strong>{status.results?.interesadas?.length || 0}</strong> Interesadas</span>
          <span><strong>{status.results?.profesionales?.length || 0}</strong> Profesionales</span>
          <span><strong>{status.results?.no_registradas?.length || 0}</strong> No registradas</span>
          <span><strong>{status.added}</strong> nuevas</span>
          <span><strong>{status.removed}</strong> retiradas</span>
          <span><strong>{status.reactivated}</strong> reactivadas</span>
        </div>
        <div className="whatsapp-tool-clusters">
          {[
            ["maestras", "Maestras"],
            ["interesadas", "Interesadas"],
            ["profesionales", "Profesionales"],
            ["no_registradas", "No registradas"],
          ].map(([key, label]) => {
            const records = status.results?.[key] || [];
            return <article key={key} className={`whatsapp-tool-cluster cluster-${key}`}>
              <header><strong>{label}</strong><span>{records.length}</span></header>
              {records.length ? <ul>{records.map((record) => <li key={`${key}-${record.celular}`}>
                <span>{record.nombre}</span>
                <small>+56 {record.celular}{key === "interesadas" ? ` · ${record.tipo}` : ""}</small>
              </li>)}</ul> : <p>Sin coincidencias.</p>}
            </article>;
          })}
        </div>
        {status.ambiguous?.length > 0 && <div className="whatsapp-tool-pending">
          <strong>Teléfonos asociados a más de un registro ({status.ambiguous.length})</strong>
          <ul>{status.ambiguous.map((item) => <li key={item.celular}>+56 {item.celular}: {item.registros.map((record) => `${record.nombre} (${record.tipo})`).join(", ")}</li>)}</ul>
        </div>}
        {status.excluded?.length > 0 && <div className="whatsapp-tool-pending">
          <strong>Registros excluidos ({status.excluded.length})</strong>
          <ul>{status.excluded.map((item) => <li key={item.celular}>+56 {item.celular}: {item.registros.join(", ")}</li>)}</ul>
        </div>}
      </section>}
    </main>
  );
}

export default WhatsappGroupUpdater;
